/**
 * One answer to "where am I in the queue", for both screens that ask.
 *
 * The kiosk confirmation and the phone status page were computing this
 * independently, from different inputs and different copy keys: the kiosk
 * from the one-shot checkInWalkIn result, the phone from the live Firestore
 * document. Two code paths describing one ticket will eventually disagree,
 * and the customer is holding both at once — the kiosk in front of them and
 * their phone in their hand. So each screen normalises what it has into
 * WalkInStatusInput and both render whatever this returns.
 *
 * Pure on purpose: the disagreement is the bug, so the resolution is the
 * part worth testing, and testing it should not require Firestore or a
 * callable.
 */

import type { CopyDict } from '../content';

export interface WalkInStatusInput {
  /** Barber whose chair is free for this customer right now, if any. */
  assignedBarber: string | null;
  /** Who they asked for. Null means "next available". */
  preferredBarber: string | null;
  /** 1-based place in line, when known. */
  position: number | null;
  /** Estimated wait. Null when we genuinely do not know. */
  minutes: number | null;
}

export interface WalkInStatusCopy {
  headline: string;
  subline: string;
  /** Rendered under the subline when a place in line is known. */
  position: string | null;
  /** True when the chair is ready — screens use it to pick their emphasis. */
  isUp: boolean;
}

type Interpolate = (template: string, facts?: Record<string, string | number>) => string;

/**
 * @param resolveName slug -> display name. Returns '' for unknown slugs, which
 *                    is why the "next barber" wording is chosen on the slug
 *                    being absent rather than on the name being empty.
 */
export function walkInStatus(
  input: WalkInStatusInput,
  copy: CopyDict['walkInStatus'],
  t: Interpolate,
  resolveName: (slug: string) => string
): WalkInStatusCopy {
  const isUp = Boolean(input.assignedBarber);
  const slug = input.assignedBarber ?? input.preferredBarber;
  // No preference is a real state, not a missing value: "in line with the
  // next barber" is the honest line when nobody was asked for by name.
  const barber = slug ? resolveName(slug) : '';
  const named = Boolean(barber);

  if (isUp) {
    return {
      headline: named
        ? t(copy.upWith, { barber })
        : t(copy.upWith, { barber: t(copy.nextBarber) }),
      subline: t(copy.upSubline),
      position: null,
      isUp: true,
    };
  }

  return {
    headline: named
      ? t(copy.inLineWith, { barber })
      : t(copy.inLineWith, { barber: t(copy.nextBarber) }),
    // Only claim a wait we actually have. Inventing "About 0 min" for an
    // unknown is worse than saying nothing about the clock.
    subline: input.minutes !== null ? t(copy.inLineSubline, { minutes: input.minutes }) : t(copy.willCall),
    position:
      input.position === null
        ? null
        : input.position === 1
          ? t(copy.positionNext)
          : t(copy.positionNth, { position: input.position }),
    isUp: false,
  };
}
